import type { HTMLAttributes } from 'react';
import { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { AvatarStub } from './avatar-stub';
import { getInitials } from './get-initials';
import { getLinearGradientColor } from './get-linear-gradient-color';

const INITIALS_FONT_SIZE_RATIO = 0.39;
const INITIALS_LINE_HEIGHT_RATIO = 0.625;

export type AvatarProps = HTMLAttributes<Element> & {
  src?: string;
  srcSet?: string;
  altText?: string;
  size?: number;
  name?: string;
  isLoading?: boolean;
  backgroundColor?: string;
};

export const Avatar = ({
  src,
  srcSet,
  name,
  altText = '',
  size = 40,
  isLoading: forceShowSkeleton = false,
  backgroundColor = 'rgb(247, 247, 247)',
  ...forwardingProps
}: AvatarProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loadingError, setLoadingError] = useState(false);

  const initials = getInitials(name);

  const loaded = !forceShowSkeleton && imageLoaded;

  let display;
  if (src && !loadingError) {
    display = 'image';
  } else if (initials) {
    display = 'initials';
  } else {
    display = 'stub';
  }

  const lightenedHslBackgroundColor = `hsl(from ${backgroundColor} h s calc(min(l + 10, 95)))`;
  const { hslFirstLinearColor, hslSecondLinearColor } =
    getLinearGradientColor(backgroundColor);

  const onLoadHandler = () => setImageLoaded(true);
  const onErrorHandler = () => setLoadingError(true);

  return (
    <Container {...forwardingProps} $size={size}>
      <Background
        $backgroundColor={backgroundColor}
        $lightenedHslBackgroundColor={lightenedHslBackgroundColor}
        $loaded={display === 'image' ? loaded : true}
      >
        {
          {
            stub: <AvatarStub backgroundColor={backgroundColor} />,
            image: (
              <StyledAvatar
                $loaded={loaded}
                loading="lazy"
                src={src}
                srcSet={srcSet}
                alt={altText}
                onLoad={onLoadHandler}
                onError={onErrorHandler}
              />
            ),
            initials: (
              <StyledInitials
                $size={size}
                $hslFirstLinearColor={hslFirstLinearColor}
                $hslSecondLinearColor={hslSecondLinearColor}
              >
                {initials}
              </StyledInitials>
            )
          }[display]
        }
      </Background>
    </Container>
  );
};

const mixinRounded = css`
  border-radius: 50%;
  overflow: hidden;
`;

const blinker = keyframes`
  from {
    opacity: 0.4;
  }
  to {
    opacity: 1;
  }
`;

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'avatar',
    element: 'container'
  })
})<{
  $size: number;
}>`
  ${({ $size }) => css`
    position: relative;
    width: ${$size}px;
    height: ${$size}px;
    flex-shrink: 0;
    ${mixinRounded}
  `}
`;

const Background = styled.div.attrs({
  className: createReadableClass({
    component: 'avatar',
    element: 'background'
  })
})<{
  $backgroundColor: string;
  $lightenedHslBackgroundColor: string;
  $loaded: boolean;
}>`
  ${({ $backgroundColor, $lightenedHslBackgroundColor, $loaded }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      180deg,
      ${$lightenedHslBackgroundColor} 0%,
      ${$backgroundColor} 100%
    );

    transition: opacity 1s ease-out;
    opacity: ${$loaded ? 1 : 0};
    ${!$loaded &&
    css`
      animation: ${blinker} 1.5s ease-in-out infinite alternate;
    `};

    ${mixinRounded}

    @media (prefers-reduced-motion: reduce), (prefers-reduced-transparency: reduce) {
      & {
        transition: none;
        animation: none;
        opacity: 1;
      }
    }

    ${$loaded &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        border-radius: inherit;
        border: 1px solid rgba(17, 17, 17, 0.05);
      }
    `};
  `}
`;

const StyledAvatar = styled.img.attrs({
  className: createReadableClass({
    component: 'avatar',
    element: 'image'
  })
})<{
  $loaded: boolean;
}>`
  ${({ $loaded }) => css`
    display: inline-block !important;
    position: static !important;
    margin: 0 !important;
    padding: 0 !important;
    max-width: none !important;
    height: inherit !important;
    width: inherit !important;
    visibility: ${$loaded ? 'visible' : 'hidden'} !important;
    object-fit: cover;
  `}
`;

const StyledInitials = styled.div.attrs({
  className: createReadableClass({
    component: 'avatar',
    element: 'initials'
  })
})<{
  $size: number;
  $hslFirstLinearColor: string;
  $hslSecondLinearColor: string;
}>`
  ${({ $size, $hslFirstLinearColor, $hslSecondLinearColor }) => css`
    font-size: ${Math.floor($size * INITIALS_FONT_SIZE_RATIO)}px;
    line-height: ${Math.floor($size * INITIALS_LINE_HEIGHT_RATIO)}px;
    font-weight: 700;
    background: linear-gradient(
      180deg,
      ${$hslFirstLinearColor} 0%,
      ${$hslSecondLinearColor} 100%
    );
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  `}
`;
